import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// The global `axios` is an instance, which has no isCancel().
import { isCancel } from 'axios';

import { clearMilestoneDetailsCache } from 'Helpers/milestoneDetailsCache';

const ProjectRoadmapContext = createContext();

export const DEFAULT_ROADMAP_SETTINGS = {
    style: 'calendar',
    view: 'quarters',
    keyOnly: false,
    firstLevel: false,
    hideEmpty: false,
    period: 'all',
    pill: 'expanded',
    listSort: 'date',
    excludedSources: [],
    excludedCategories: [],
    collapsed: {},
    anchor: null,
    scrollTop: null,
    listScrollTop: null,
    criticalPath: 'off',
    criticalTarget: null,
};

// Only these change what the server returns; everything else is applied client-side.
const DATA_KEYS = ['view', 'keyOnly', 'firstLevel', 'hideEmpty', 'period', 'excludedSources', 'excludedCategories', 'criticalPath', 'criticalTarget'];

/**
 * Whether the style changes what the server returns: with a Period selected the Calendar shows
 * every milestone in the columns it draws and the List only those in the exact window, and with
 * First-level categories only the Calendar rolls sub-categories up while the List keeps them.
 */
export const styleChangesData = (settings) => settings.period !== 'all' || settings.firstLevel;

// Changes to these save silently, without the "View settings saved" confirmation.
const SCROLL_KEYS = ['anchor', 'scrollTop', 'listScrollTop'];

const SAVE_DEBOUNCE_MS = 600;

const KEEPALIVE_BODY_LIMIT = 60000;

const PDF_SCALING_STORAGE_KEY = 'project_roadmap_last_pdf_scaling';

const storageKey = (eventId, userId) => `project-roadmap-settings-v1-${userId ?? 'guest'}-${eventId}`;

// Browser storage can throw (private mode, blocked site data) — it is only a fallback.
const readStorage = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Nothing to do: the server copy is the source of truth.
    }
};

const removeStorage = (key) => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Nothing to do: a leftover key is harmless.
    }
};

// Settings stored before the key was scoped to the user.
const legacyStorageKey = (eventId) => `project-roadmap-settings-v1-${eventId}`;

const normalise = (settings) => {
    const merged = { ...DEFAULT_ROADMAP_SETTINGS, ...(settings || {}) };

    // An empty PHP map serialises as a JSON list.
    if (!merged.collapsed || Array.isArray(merged.collapsed)) {
        merged.collapsed = {};
    }

    merged.excludedSources = Array.isArray(merged.excludedSources) ? merged.excludedSources : [];
    merged.excludedCategories = Array.isArray(merged.excludedCategories) ? merged.excludedCategories : [];

    return merged;
};

const initialSettings = (serverSettings, eventId, userId) => {
    removeStorage(legacyStorageKey(eventId));

    if (serverSettings) {
        return normalise(serverSettings);
    }

    const stored = readStorage(storageKey(eventId, userId));

    if (stored) {
        try {
            return normalise(JSON.parse(stored));
        } catch {
            // Fall through to the defaults.
        }
    }

    return normalise(null);
};

const pick = (settings, keys) => Object.fromEntries(keys.map((key) => [key, settings[key]]));

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

/**
 * Per-event roadmap state that outlives the provider. The Project Management tabs unmount the
 * Roadmap when another tab is opened; the settings, the last response and any unsent save are
 * kept here for the life of the page so returning to the tab resumes where it was left.
 *
 * Saves are serialised: while one is in flight, later changes wait and the latest settings are
 * sent once it resolves, so an older write can never land last.
 */
const sessions = new Map();

const createSession = (sessionKey, settings) => {
    const session = {
        settings,
        data: null,
        saveUrl: null,
        timer: null,
        pending: false,
        inFlight: false,
        controller: null,
        flash: false,
        onSaved: null,
        // Views holding a debounced scroll position hand it over before the page goes away.
        positionFlushers: new Set(),
    };

    session.send = () => {
        clearTimeout(session.timer);
        session.timer = null;

        if (!session.saveUrl || session.inFlight) {
            return;
        }

        const flash = session.flash;
        session.pending = false;
        session.flash = false;
        session.inFlight = true;
        session.controller = new AbortController();

        axios
            .put(session.saveUrl, { settings: session.settings }, { signal: session.controller.signal })
            .then(() => {
                if (flash && session.onSaved) {
                    session.onSaved();
                }
            })
            .catch(() => {
                // localStorage already holds the latest copy; the next change retries the save.
            })
            .finally(() => {
                session.inFlight = false;

                if (session.pending) {
                    session.send();
                }
            });
    };

    session.schedule = (flash) => {
        session.pending = true;
        session.flash = session.flash || flash;
        clearTimeout(session.timer);
        session.timer = setTimeout(session.send, SAVE_DEBOUNCE_MS);
    };

    session.flush = () => {
        if (session.pending) {
            session.send();
        }
    };

    // The page is going away: an XHR would be cancelled, a keepalive fetch is not.
    session.flushOnPageHide = () => {
        if (!session.pending || !session.saveUrl) {
            return;
        }

        const body = JSON.stringify({ settings: session.settings });

        // Browsers refuse keepalive bodies over 64KB; leave such a save pending.
        if (new Blob([body]).size >= KEEPALIVE_BODY_LIMIT) {
            return;
        }

        clearTimeout(session.timer);
        session.pending = false;

        // Stop waiting on the older save; if it already reached the server, the row lock applies it before this newer one.
        session.controller?.abort();

        fetch(session.saveUrl, {
            method: 'PUT',
            keepalive: true,
            credentials: 'same-origin',
            headers: {
                'X-CSRF-TOKEN': csrfToken(),
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body,
        }).catch(() => {});
    };

    sessions.set(sessionKey, session);

    return session;
};

const getSession = (eventId, userId, serverSettings) => {
    const sessionKey = storageKey(eventId, userId);

    return sessions.get(sessionKey) || createSession(sessionKey, initialSettings(serverSettings, eventId, userId));
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () =>
        sessions.forEach((session) => {
            session.positionFlushers.forEach((flushPosition) => flushPosition());
            session.flushOnPageHide();
        }),
    );
}

export const ProjectRoadmapProvider = ({ eventId, userId, serverSettings, fetchUrl, milestoneUrl, saveUrl, downloadPdfUrl, children }) => {
    const [session] = useState(() => getSession(eventId, userId, serverSettings));
    session.saveUrl = saveUrl;

    const [settings, setSettings] = useState(session.settings);
    const [data, setData] = useState(session.data);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [lastPdfScaling, setLastPdfScaling] = useState(() => readStorage(PDF_SCALING_STORAGE_KEY) || 'multi-page');

    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    const mountedRef = useRef(true);

    // The calendar registers { captureAnchor, requestRestore, goToToday } while it is mounted.
    const scrollerRef = useRef(null);
    const registerScroller = useCallback((scroller) => {
        scrollerRef.current = scroller;
    }, []);

    // Returns the unregister function, for an effect cleanup.
    const registerPositionFlusher = useCallback(
        (flushPosition) => {
            session.positionFlushers.add(flushPosition);

            return () => session.positionFlushers.delete(flushPosition);
        },
        [session],
    );

    // Leaving the tab sends any unsent change now rather than dropping it.
    useEffect(() => {
        mountedRef.current = true;
        session.onSaved = () => setSavedAt(Date.now());

        return () => {
            mountedRef.current = false;
            session.onSaved = null;
            session.flush();
        };
    }, [session]);

    /**
     * Patch the settings. A change that refetches first captures the date at the calendar's left
     * edge (a new Period resets it) so the view can be put back once the new columns commit.
     *
     * Safe to call while unmounting: the session keeps the change and saves it.
     */
    const update = useCallback(
        (patch) => {
            const current = session.settings;
            const changed = Object.keys(patch).filter((key) => !sameValue(patch[key], current[key]));

            if (!changed.length) {
                return;
            }

            let next = { ...current, ...patch };

            if (changed.some((key) => DATA_KEYS.includes(key)) && scrollerRef.current) {
                const anchor = changed.includes('period') ? null : scrollerRef.current.captureAnchor();
                scrollerRef.current.requestRestore(anchor);
                next = { ...next, anchor };
            }

            session.settings = next;
            settingsRef.current = next;
            writeStorage(storageKey(eventId, userId), JSON.stringify(next));

            if (mountedRef.current) {
                setSettings(next);
            }

            session.schedule(!changed.every((key) => SCROLL_KEYS.includes(key)));
        },
        [session, eventId, userId],
    );

    // ── Data ──
    const dataKey = JSON.stringify(pick(settings, styleChangesData(settings) ? ['style', ...DATA_KEYS] : DATA_KEYS));

    // Bumped to rerun the fetch with the current settings.
    const [fetchAttempt, setFetchAttempt] = useState(0);
    const retry = useCallback(() => setFetchAttempt((attempt) => attempt + 1), []);

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(null);
        // Also cleared on success below, so a response that arrives after this effect re-runs
        // can't leave stale details behind; clearing here covers a request that never resolves
        // (left in flight or failed) and the stretch of time before it does.
        clearMilestoneDetailsCache();

        axios
            .post(fetchUrl, { settings: JSON.parse(dataKey) }, { signal: controller.signal })
            .then((response) => {
                clearMilestoneDetailsCache();
                session.data = response.data;
                setData(response.data);
                setLoading(false);
            })
            .catch((requestError) => {
                if (isCancel(requestError)) {
                    return;
                }

                setError(requestError);
                setLoading(false);
            });

        return () => controller.abort();
    }, [fetchUrl, dataKey, session, fetchAttempt]);

    // ── Row view ──
    const toggleCollapsed = useCallback(
        (key) => {
            const collapsed = { ...session.settings.collapsed };

            if (collapsed[key]) {
                delete collapsed[key];
            } else {
                collapsed[key] = true;
            }

            update({ collapsed });
        },
        [session, update],
    );

    const expandAll = useCallback(() => update({ collapsed: {} }), [update]);

    const collapseAll = useCallback(() => {
        const collapsed = { ...session.settings.collapsed };

        (data?.tree?.sources || []).forEach((source) => {
            collapsed[`source:${source.key}`] = true;
        });

        update({ collapsed });
    }, [data, session, update]);

    const resetFilters = useCallback(() => update({ keyOnly: false, period: 'all', excludedSources: [], excludedCategories: [] }), [update]);

    const goToToday = useCallback(() => scrollerRef.current?.goToToday(), []);

    // ── PDF ──
    const downloadPdf = useCallback(
        async (scaling) => {
            if (!downloadPdfUrl || pdfDownloading) {
                return;
            }

            const scalingToUse = scaling || lastPdfScaling;
            setPdfDownloading(true);
            setLastPdfScaling(scalingToUse);
            writeStorage(PDF_SCALING_STORAGE_KEY, scalingToUse);

            try {
                const response = await axios.post(downloadPdfUrl, { settings: settingsRef.current, scaling: scalingToUse }, { responseType: 'blob' });

                const disposition = response.headers['content-disposition'] || '';
                const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'Milestone_Roadmap.pdf';

                const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = blobUrl;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            } catch (downloadError) {
                console.error('PDF download failed', downloadError);
            } finally {
                setPdfDownloading(false);
            }
        },
        [downloadPdfUrl, pdfDownloading, lastPdfScaling],
    );

    const value = useMemo(
        () => ({
            settings,
            data,
            loading,
            error,
            retry,
            savedAt,
            update,
            toggleCollapsed,
            expandAll,
            collapseAll,
            resetFilters,
            registerScroller,
            registerPositionFlusher,
            goToToday,
            downloadPdf,
            pdfDownloading,
            lastPdfScaling,
            milestoneUrl,
        }),
        [
            settings,
            data,
            loading,
            error,
            retry,
            savedAt,
            update,
            toggleCollapsed,
            expandAll,
            collapseAll,
            resetFilters,
            registerScroller,
            registerPositionFlusher,
            goToToday,
            downloadPdf,
            pdfDownloading,
            lastPdfScaling,
            milestoneUrl,
        ],
    );

    return <ProjectRoadmapContext.Provider value={value}>{children}</ProjectRoadmapContext.Provider>;
};

export default ProjectRoadmapContext;
